import { Component, Element, h, Host, Listen, Prop, State } from "@stencil/core";
import { checkFileType } from "./utils/checkFileType";
// this functions offer compatibility for browsers that don't support the
//DataTransfer() constructor. Delete them and related logic when no longer needed.
import { checkForInvalidFileExtension, checkForInvalidFileSize, checkForTooManyFiles } from "./utils/IE11";

let fileInputIdCounter = 0;

@Component({
    tag: "ux-file-upload",
    styleUrl: "file-upload.scss",
    shadow: false
})
export class FileUpload {
    @Element() element: HTMLUxFileUploadElement;
    private baseClass = "ux-file-upload";
    private fileUploadField: HTMLInputElement | null = null;
    private fileContainer: HTMLDivElement | null = null;
    private fileInputIdentifier = "data-file-input-id";
    private thumbnailContainer: HTMLDivElement | null = null;

    /** Option to send multiple files in one request */
    @Prop() readonly maxFiles: number = 3;
    /** The maximum upload file size in MB */
    @Prop() readonly maxFileSize: number = 20;
    /** Accepted file types in comma separated list of mime types or file extensions  */
    @Prop() readonly acceptedFiles: string = "image/*,application/pdf";
    /** Message Upload limit reached   */
    @Prop() readonly messageMaxFilesReached: string = "Upload limit reached";
    /** Message Upload limit reached   */
    @Prop() readonly messageMaxFileSize: string = "One of the uploaded files was too big";
    /** Message invalid file extension */
    @Prop() readonly messageInvalidFile: string = "One of the uploaded files did not have the right file extension";
    /** generic error message to be displayed */
    @Prop({ mutable: true }) errorMsg = "";
    /** Label text describing the file upload field */
    @Prop() readonly label: string = "";
    /** indicates if the file upload is disabled */
    @Prop() readonly disabled: boolean = false;
    /** indicates if the file upload is required  */
    @Prop() readonly required: boolean = false;
    /** indicates if the file upload has an error state. Use error attribute only for static error rendering.  */
    @Prop({ mutable: true }) error = false;
    /** name of the input field */
    @Prop() readonly name: string = "";
    /** indicates if component has bulk delete functionality */
    @Prop() readonly bulkDelete: boolean = false;

    private effectiveAllowedSize: number;
    private allowedFileTypes: string[];

    @State() message = "";
    @State() uploadedFilesCount = 0;
    @State() supportsIndividualInputs: boolean = typeof DataTransfer === "function";

    componentWillLoad() {
        this.effectiveAllowedSize = this.maxFileSize * 1024 * 1024;
        this.allowedFileTypes = this.acceptedFiles.split(",");
    }

    connectedCallback() {
        this.element.addEventListener("change", this.onInputChange);
        this.element.addEventListener("dragenter", this.onDragEnter);
        this.element.addEventListener("dragleave", this.onDragLeave);
        this.element.addEventListener("dragover", this.stopEvent);
        this.element.addEventListener("drop", this.handleDrop);
    }

    private onDragLeave = (e: Event) => {
        this.stopEvent(e);
    };

    private onDragEnter = (e: Event) => {
        this.stopEvent(e);
    };

    private stopEvent = (e: Event) => {
        e.stopPropagation();
        e.preventDefault();
    };

    private handleDrop = (e: DragEvent) => {
        this.stopEvent(e);
        const dt = e.dataTransfer;
        const files = dt.files;
        this.checkAndAddFiles(files);
    };

    private onInputChange = () => {
        const files = this.fileUploadField.files;
        this.checkAndAddFiles(files);
    };

    private resetFileUploadField = () => {
        this.fileUploadField.files = null;
        this.fileUploadField.value = "";
    };

    private checkAndAddFiles = (fileList: FileList) => {
        if (this.supportsIndividualInputs) {
            this.checkAndAddIndividualFiles(fileList);
            this.resetFileUploadField();
        } else {
            if (!checkForTooManyFiles(fileList, this.maxFiles, this.uploadedFilesCount)) {
                this.error = true;
                this.errorMsg = this.messageMaxFilesReached;
                this.fileUploadField.value = "";
            } else if (!checkForInvalidFileExtension(fileList, this.acceptedFiles)) {
                this.error = true;
                this.errorMsg = this.messageInvalidFile;
                this.fileUploadField.value = "";
            } else if (!checkForInvalidFileSize(fileList, this.effectiveAllowedSize)) {
                this.error = true;
                this.errorMsg = this.messageMaxFileSize;
                this.fileUploadField.value = "";
            }
        }
        this.updateUploadedFilesCount();
    };

    private checkAndAddIndividualFiles = (fileList: FileList) => {
        if (!this.fileContainer) {
            return;
        }
        for (let i = 0; i < fileList.length; i++) {
            if (this.maxFiles === this.uploadedFilesCount) {
                this.error = true;
                this.errorMsg = this.messageMaxFilesReached;
            } else if (fileList[i].size > this.effectiveAllowedSize) {
                this.error = true;
                this.errorMsg = this.messageMaxFileSize;
            } else if (!checkFileType(fileList[i], this.allowedFileTypes)) {
                this.error = true;
                this.errorMsg = this.messageInvalidFile;
            } else {
                const dataTransfer = new DataTransfer();
                dataTransfer.items.add(fileList.item(i));
                const newFileIput = document.createElement("input");
                newFileIput.setAttribute("type", "file");
                newFileIput.setAttribute(this.fileInputIdentifier, String(fileInputIdCounter++));
                newFileIput.setAttribute("name", this.name);
                newFileIput.files = dataTransfer.files;
                this.fileContainer.insertAdjacentElement("beforeend", newFileIput);
                this.updateUploadedFilesCount();
            }
        }
    };

    private updateUploadedFilesCount = () => {
        let totalFiles = 0;
        if (this.supportsIndividualInputs) {
            const fileInputs = this.fileContainer.querySelectorAll("input");
            if (fileInputs.length === 0) {
                this.uploadedFilesCount = 0;
            }
            fileInputs.forEach((file) => {
                totalFiles = totalFiles + file.files.length;
            });
        } else {
            totalFiles = this.fileUploadField.files.length;
        }
        this.uploadedFilesCount = totalFiles;
    };

    private renderThumbnails = (): HTMLElement[] => {
        if (this.fileContainer === null) {
            return;
        }
        const thumbnails: HTMLElement[] = [];
        const inputs = this.fileContainer.querySelectorAll("input");
        if (inputs.length === 0) {
            return;
        }
        inputs.forEach((input: HTMLInputElement) => {
            const files = input.files;
            /* eslint-disable-next-line @typescript-eslint/prefer-for-of */
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                thumbnails.push(
                    <div
                        class={{
                            [`${this.baseClass}__thumbnail-container`]: true
                        }}
                    >
                        <ux-file-upload-thumbnail
                            key={file.name}
                            file-type={file.type}
                            file={file}
                            is-individual={this.supportsIndividualInputs}
                            file-input-id={input.getAttribute(this.fileInputIdentifier)}
                            file-name={file.name}
                        />
                    </div>
                );
            }
        });
        if (thumbnails.length === 0) {
            return;
        }
        return thumbnails;
    };

    private renderLegacyThumbnails = (): HTMLElement[] => {
        if (this.fileUploadField === null) {
            return;
        }
        const thumbnails: HTMLElement[] = [];
        const selectedFiles = this.fileUploadField.files;
        /* eslint-disable-next-line @typescript-eslint/prefer-for-of */
        for (let i = 0; i < selectedFiles.length; i++) {
            const file = selectedFiles[i];
            thumbnails.push(
                <div
                    class={{
                        [`${this.baseClass}__thumbnail-container`]: true
                    }}
                >
                    <ux-file-upload-thumbnail
                        key={file.name}
                        file-type={file.type}
                        file={file}
                        is-individual={this.supportsIndividualInputs}
                        file-name={file.name}
                    />
                </div>
            );
        }
        if (thumbnails.length === 0) {
            return;
        }
        return thumbnails;
    };

    @Listen("uxFileUploadDelete", { target: "window" })
    deleteInput(e: CustomEvent) {
        if ((e.target as HTMLElement).parentElement.parentElement === this.thumbnailContainer) {
            if (this.supportsIndividualInputs) {
                const inputToDelete = this.fileContainer.querySelector(
                    `input[${this.fileInputIdentifier}="${e.detail.fileInputId}"]`
                ) as HTMLInputElement;
                if (inputToDelete === null) {
                    return;
                }
                inputToDelete.remove();
            } else {
                this.fileUploadField.value = "";
            }
        }
        this.updateUploadedFilesCount();
    }

    @Listen("focusout", { target: "window" })
    resetErrorOnBlur(e: Event) {
        const eTarget = e.target as HTMLElement;
        if (eTarget.closest("ux-file-upload") !== this.element) {
            return;
        }
        this.error = false;
    }

    private deleteAllFiles = (e: Event) => {
        e.preventDefault();
        if (this.supportsIndividualInputs) {
            this.fileUploadField.value = "";
            const fileInputs = this.fileContainer.querySelectorAll("input");
            if (fileInputs.length === 0) {
                return;
            }
            fileInputs.forEach((fileInput) => fileInput.remove());
            this.updateUploadedFilesCount();
        } else {
            this.fileUploadField.value = "";
            this.updateUploadedFilesCount();
        }
    };

    private checkItemsPerRow = () => {
        return getComputedStyle(this.element).getPropertyValue("--ux-file-upload-items-per-row");
    };

    render() {
        const { label, disabled, required, error, errorMsg } = this;

        return (
            <Host
                class={{
                    [`${this.baseClass}`]: true,
                    [`${this.baseClass}--disabled`]: disabled,
                    [`${this.baseClass}--error`]: error
                }}
            >
                {(label !== undefined || (error && errorMsg !== "")) && (
                    <div class="ux-label-container">
                        {label !== "" && (
                            <label class="ux-label">
                                {label}
                                {required && <span> *</span>}
                            </label>
                        )}{" "}
                        {error && errorMsg !== "" && <span class={`ux-label ux-label--message ux-label--error`}>{errorMsg}</span>}
                    </div>
                )}
                <div class={`${this.baseClass}__file-container`} ref={(el) => (this.fileContainer = el)}></div>
                <div
                    class={{
                        [`${this.baseClass}__container`]: true,
                        [`${this.baseClass}__container--bulk-delete`]: this.bulkDelete,
                        [`${this.baseClass}__container--items-per-row`]: this.checkItemsPerRow() !== "auto"
                    }}
                    ref={(el) => (this.thumbnailContainer = el)}
                >
                    {h(this.supportsIndividualInputs ? this.renderThumbnails : this.renderLegacyThumbnails)}
                    <div
                        class={{
                            [`${this.baseClass}__action-container`]: true
                        }}
                    >
                        <div
                            class={{
                                [`${this.baseClass}__action-container-inner`]: true
                            }}
                        >
                            <div
                                class={{
                                    [`${this.baseClass}__add-file`]: true,
                                    [`${this.baseClass}__add-file--with-thumbnails`]: this.uploadedFilesCount !== 0
                                }}
                            >
                                <ux-icon name="cross" weight="normal"></ux-icon>
                            </div>
                            {this.uploadedFilesCount !== 0 && this.bulkDelete && (
                                <button class={`${this.baseClass}__delete-all`} onClick={this.deleteAllFiles}>
                                    <ux-icon name="bin" weight="normal"></ux-icon>
                                </button>
                            )}
                        </div>
                    </div>
                    <label class={`${this.baseClass}__label`}>
                        <input
                            type="file"
                            name={this.supportsIndividualInputs && this.uploadedFilesCount > 0 ? null : this.name}
                            multiple={this.maxFiles > 1 ? true : null}
                            class={`${this.baseClass}__input-field`}
                            ref={(el) => (this.fileUploadField = el)}
                            onFocus={() => (this.error = false)}
                            required={this.required && !this.uploadedFilesCount}
                            aria-required={this.required}
                            aria-invalid={error ? "true" : "false"}
                            accept={this.acceptedFiles}
                        />
                    </label>
                </div>
            </Host>
        );
    }
}
